import React, { useState, useEffect } from "react";
import api from "./api";
import { useAuth } from "./AuthContext";
import { FaDownload } from "react-icons/fa";
import { DateTime } from "luxon";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

function DownloadPage() {
  const { user, isAdmin } = useAuth();

  const defaultDateFrom = DateTime.now().startOf("year").toISODate();
  const defaultDateTo = DateTime.now().toISODate();

  const [companies, setCompanies] = useState([]);
  const [companyName, setCompanyName] = useState(
    isAdmin ? "" : user.companyName
  );
  const [format, setFormat] = useState("dbf");
  const [version, setVersion] = useState("detailed");
  const [dateField, setDateField] = useState("prometa");
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Which date the range applies to. Delivery date (datum prometa) is the
  // VAT-relevant default; the others help locate invoices that fall outside
  // that window (or have no delivery date).
  const dateFieldLabels = {
    prometa: "datum prometa",
    izdavanja: "datum izdavanja",
    prijema: "datum prijema",
  };

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/api/listCompaniesFun")
      .then((res) => {
        setCompanies(res.data);
        if (res.data.length > 0) setCompanyName(res.data[0]);
      })
      .catch((err) => console.error("Failed to load companies:", err));
  }, [isAdmin]);

  const handleDownload = async () => {
    setLoading(true);
    setMessage("Preuzimanje...");
    const url = `/api/downloadDataFun?companyName=${companyName}&format=${format}&version=${version}&dateField=${dateField}&dateFrom=${dateFrom}&dateTo=${dateTo}`;
    const dateTimeNow = DateTime.now().toFormat("dd-MM-yy-HH-mm");
    const fileName = `${companyName}${dateTimeNow}.${format}`;

    try {
      const response = await api.get(url, { responseType: "blob" });
      const contentType = response.headers["content-type"] || "";

      // The backend returns JSON (not a file) when there is no data for the
      // chosen company/period. Read the message and show it instead of
      // downloading an empty file.
      if (contentType.includes("application/json")) {
        const text = await response.data.text();
        let msg = "Nema podataka za izabrani period.";
        try {
          const parsed = JSON.parse(text);
          if (parsed.message) msg = parsed.message;
        } catch (e) {
          /* keep default message */
        }
        setMessage(msg);
        setLoading(false);
        return;
      }

      const blob = new Blob([response.data], {
        type: contentType,
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      setMessage("Preuzimanje zavrseno");
    } catch (error) {
      console.error("Error:", error);
      const status = error.response?.status;
      setMessage(
        !status || status >= 500
          ? "Greška na serveru ili vezi. Pokušajte ponovo za par sekundi."
          : "Preuzimanje neuspesno"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-card">
      <h2 className="page-title">Preuzimanje podataka</h2>
      <form>
        {isAdmin ? (
          <div className="form-group">
            <label>Ime firme</label>
            <select
              className="form-control"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            >
              {companies.map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="form-group">
            <label>Firma</label>
            <input
              type="text"
              className="form-control"
              value={user.companyName}
              disabled
            />
          </div>
        )}
        <div className="form-group">
          <label>Verzija izveštaja</label>
          <select
            className="form-control"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
          >
            <option value="detailed">Detaljna (sa artiklima)</option>
            <option value="summary">Zbirna (PDV 10% i 20%)</option>
          </select>
        </div>
        <div className="form-group">
          <label>Filtriraj po datumu</label>
          <select
            className="form-control"
            value={dateField}
            onChange={(e) => setDateField(e.target.value)}
          >
            <option value="prometa">Datum prometa (za PDV)</option>
            <option value="izdavanja">Datum izdavanja</option>
            <option value="prijema">Datum prijema</option>
          </select>
        </div>
        <div className="form-group">
          <label>Od ({dateFieldLabels[dateField]})</label>
          <Flatpickr
            className="form-control flatpickr-input"
            value={dateFrom}
            onChange={(date) =>
              setDateFrom(DateTime.fromJSDate(date[0]).toISODate())
            }
            options={{ dateFormat: "Y-m-d" }}
          />
        </div>
        <div className="form-group">
          <label>Do ({dateFieldLabels[dateField]})</label>
          <Flatpickr
            className="form-control flatpickr-input"
            value={dateTo}
            onChange={(date) =>
              setDateTo(DateTime.fromJSDate(date[0]).toISODate())
            }
            options={{ dateFormat: "Y-m-d" }}
          />
        </div>
        <div className="form-group">
          <label>Format</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="format"
                checked={format === "dbf"}
                onChange={() => setFormat("dbf")}
              />
              DBF
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="format"
                checked={format === "xlsx"}
                onChange={() => setFormat("xlsx")}
              />
              XLSX
            </label>
          </div>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={handleDownload}
          disabled={loading}
        >
          {loading ? (
            "Preuzimanje..."
          ) : (
            <>
              <FaDownload style={{ marginRight: 8 }} /> Preuzmi
            </>
          )}
        </button>
      </form>
      {message && (
        <div
          className={`status-message ${
            message === "Preuzimanje zavrseno" ? "success" : "error"
          }`}
        >
          {message}
        </div>
      )}
    </div>
  );
}

export default DownloadPage;
